import { traverserStopClassName } from "_shared/_views/highlights/traverser-stop-scope";
import Document from "documents/document";
import Divider from "@mui/material/Divider";
import { keyframes } from "@mui/material";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import AHBoolean from "_shared/booleans/boolean";
import Guid from "_shared/guids/guid";
import React from "react";
import ProposalIssues from "work/proposals/issues/proposal-issues";
import WorkDocument from "work/work-agreements/contracts/work-document";
import ProposalIssueList from "work/proposals/issues/_views/proposal-issue-list";
import ProposalFieldHistoryButton from "work/proposals/negotiation/proposal-field-history-button";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import { ReviewerEditLockIcon } from "work/proposals/reviewers/_views/reviewer-edit-lock-feedback";
import DocumentRedlineSelection from "work/proposals/redlining/field-redlines/_views/document-redline-selection";
import SwitchRedline from "work/proposals/redlining/field-redlines/_views/switch-redline";
import FieldRedline, { FieldRedlineArray } from "work/proposals/redlining/field-redlines/field-redline";
import ProposalRedline from "work/proposals/redlining/proposal-redline";
import downloadFile from "_shared/_utilities/download-file";
import { useLazyDownloadDocumentQuery } from "documents/_api/document-api";
import { showErrorSnackbar } from "_shared/_errors/error-snackbar";
import DocumentSelector from "documents/_views/selectors/document-selector";
import DocumentUploadOverlay from "documents/_views/uploader/document-upload-overlay";
import { WorkDocumentType } from "work/work-agreements/contracts/work-document-type";
import { Masked } from "work/masked-fields";
import ResizableTemplatePanel from "work/proposals/_views/_shared/resizable-template-panel";
import getReplacementDocumentTemplateIds from "work/proposals/_views/active/tabs/replacement-document-template-ids";
import { MaskedProposalField, maskedProposalFieldNotice } from "work/proposals/_views/_shared/masked-field-notice";
import {
  paneIsCompact,
  workPaneProseInset,
} from "work/_views/work-pane-layout";
import { useWorkPaneIsCompact } from "work/_views/use-work-pane-size";

const pulseBorder = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
  }
`;
const DOCUMENT_ROW_MAX_WIDTH = "550px";

const ListContainer = styled(Grid)(({ theme }) => ({
  flex: 1,
  flexWrap: "nowrap",
  height: "100%",
  minHeight: "20rem",
  width: "100%",
  [paneIsCompact(theme)]: {
    flexDirection: "column",
    flexWrap: "nowrap",
    height: "auto",
    minHeight: 0,
  },
}));
/**
 * The templates column, dimmed while the collection it feeds is waived.
 *
 * Only the dimming lives here. `ResizableTemplatePanel` owns the column's width, its collapse to a
 * rail when a side pane opens, and what it becomes on a compact pane — the same arrangement the fee
 * schedule and team tabs get, which this tab used to half-copy from its own styles.
 */
const DocumentSelectorContainer = styled(ResizableTemplatePanel, {
  shouldForwardProp: (prop) => prop !== "waived",
})<{ waived?: boolean }>(({ waived }) => ({
  opacity: waived ? 0.25 : 1,
  pointerEvents: waived ? "none" : "auto",
}));
const SelectorDivider = styled(Grid, {
  shouldForwardProp: (prop) => prop !== "waived",
})<{ waived?: boolean }>(({ theme, waived }) => ({
  opacity: waived ? 0.25 : 1,
  pointerEvents: waived ? "none" : "auto",
  [paneIsCompact(theme)]: {
    display: "none",
  },
}));
const SelectedDocsList = styled(DocumentRedlineSelection)(({ theme }) => ({
  // No top margin under a narrower pane: it stood the collection's title 16px further off the switch
  // or notice above it than anything else on the tab (#6041).
  flex: 1,
  maxHeight: "60vh",
  // No left margin: the table meets the resize divider, the way the fee schedule's does. The prose
  // above it carries its own inset instead — see `workPaneProseInset`.
  minWidth: 0,
  position: "relative",
  // #4955: the conflict documents are a table now, and it caps its own row width and drops that cap on
  // a narrow screen. What used to be said here about `.MuiListItem-root`, the list's subheader and the
  // inline secondary action has no shape left to address.
  [paneIsCompact(theme)]: {
    boxSizing: "border-box",
    marginLeft: 0,
    maxHeight: "none",
    maxWidth: "none",
    minWidth: 0,
    padding: 0,
    width: "100%",
    "&&": {
      marginLeft: 0,
      maxWidth: "none",
      minWidth: 0,
      padding: 0,
      width: "100%",
    },
  },
}));
const HeadControls = styled("div")(({ theme }) => ({
  ...workPaneProseInset(theme),
  alignItems: "flex-start",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  maxWidth: DOCUMENT_ROW_MAX_WIDTH,
  paddingTop: theme.spacing(1),
  width: "100%",
  [paneIsCompact(theme)]: {
    maxWidth: "none",
  },
}));
const SwitchControls = styled("div")(({ theme }) => ({
  [paneIsCompact(theme)]: {
    paddingRight: 0,
  },
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  paddingRight: theme.spacing(1),
  width: "100%",
}));
const FieldActions = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  flexShrink: 0,
}));
const Uploader = styled(DocumentUploadOverlay)(() => ({
  backgroundColor: "rgba(250, 250, 250, 0.5)",
  backdropFilter: "blur(5px) saturate(200%)",
  position: "absolute",
  top: 0,
  left: 0,
  margin: "1rem",
  width: "calc(100% - 2rem)",
  height: "calc(100% - 2rem)",
  zIndex: 9999,
}));
const Change = styled("div", { shouldForwardProp: (prop) => prop !== "active" })<{ active: boolean }>(
  ({ theme, active }) => ({
    alignItems: "center",
    animation: active ? `${pulseBorder} 1.5s infinite` : "none",
    borderRadius: theme.spacing(0.5),
    display: "inline-flex",
    gap: theme.spacing(1),
    outline: active ? `1px solid ${theme.palette.primary.main}` : 0,
    outlineOffset: active ? theme.spacing(0.5) : 0,
  })
);
const WaivedContent = styled("div", {
  shouldForwardProp: (prop) => prop !== "waived",
})<{ waived: boolean }>(({ waived }) => ({
  display: "flex",
  flex: 1,
  minWidth: 0,
  opacity: waived ? 0.25 : 1,
  pointerEvents: waived ? "none" : "auto",
  width: "100%",
}));
const SelectedDocsPanel = styled("div")(() => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minWidth: 0,
  width: "100%",
}));

type ConflictsTabProps = {
  proposalId?: Guid;
  proposalRedline: ProposalRedline;
  issues?: ProposalIssues;
  activeReviewField: ProposalField | undefined;
  negotiationId?: string;
  viewerScope?: string;
  disableEditing?: boolean;
  fieldLockTooltip?: string;
  /** A side pane is taking the row's width, so the templates column gives up its own — see `ResizableTemplatePanel`. */
  isDrawerOpen: boolean;
  onProposalRedlineChange: (newRedline: ProposalRedline, traversalFieldOverride?: ProposalField | null) => void;
  onTraverseToNewField: (fieldInfo: ProposalField | undefined) => void;
};

const ConflictsTab = (props: Readonly<ConflictsTabProps>) => {
  const {
    proposalRedline,
    proposalId,
    activeReviewField,
    negotiationId,
    viewerScope,
    disableEditing,
    isDrawerOpen,
    fieldLockTooltip,
    onProposalRedlineChange,
    onTraverseToNewField,
  } = props;

  // The template selector collapses to its summary when there is no room to hold it open. The pane
  // is what decides that here, not the window — an open comments panel takes a column out of it.
  const selectorIsCompact = useWorkPaneIsCompact();
  const [isDownloadingFile, setIsDownloadingFile] = React.useState<Guid | null>(null);
  const [documentIdToReplace, setDocumentIdToReplace] = React.useState<Guid | null>(null);
  const [creatingDocumentFromTemplate, setCreatingDocumentFromTemplate] = React.useState<Guid | null>(null);

  const issues = React.useMemo(
    () =>
      props.issues?.entries.filter((issue) =>
        [
          ProposalField.Conflicts,
          ProposalField.ConflictsDocument(issue.field.id ?? Guid.EMPTY),
          ProposalField.WaiveConflictsCheck,
        ].some((candidate) => candidate.isEqualTo(issue.field))
      ),
    [props.issues?.entries]
  );

  const [downloadDocument] = useLazyDownloadDocumentQuery();

  async function handleDownloadDocumentById(
    documentId?: Guid,
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): Promise<void> {
    event?.stopPropagation();
    if (!documentId) return;

    try {
      setIsDownloadingFile(documentId);
      const conflictsTemplate = await downloadDocument({ documentId: documentId.value }).unwrap();
      await downloadFile(conflictsTemplate);
    } catch (err) {
      console.error(err);
      showErrorSnackbar(err, { doing: "downloading the conflicts template", operation: "proposal.conflicts.download" });
    } finally {
      setIsDownloadingFile(null);
    }
  }

  function handleDocumentRedlineChanged(
    newDocumentsRedline: FieldRedlineArray<WorkDocument>,
    traversalFieldOverride?: ProposalField
  ) {
    let updatedProposalRedline = proposalRedline.updateConflictsRedline(newDocumentsRedline);
    if (Masked.isMasked(updatedProposalRedline.conflictsCheckWaived))
      throw new Error("Conflicts Check Waived cannot be a Masked Contract");

    if (
      newDocumentsRedline.redlines.some((documentRedline) => documentRedline.currentEntry) &&
      !updatedProposalRedline.conflictsCheckWaived.currentEntry?.isEqualTo(new AHBoolean(false))
    ) {
      updatedProposalRedline = updatedProposalRedline.updateConflictsCheckWaivedRedline(
        updatedProposalRedline.conflictsCheckWaived.edit(new AHBoolean(false))
      );
    }
    onProposalRedlineChange(updatedProposalRedline, traversalFieldOverride);
  }

  function handleDocumentSelectionChanged(selectedDocs: Document[], deselectedDocs: Document[]) {
    if (!selectedDocs.length && !deselectedDocs.length) return;

    let updatedProposalRedline = proposalRedline;
    if (Masked.isMasked(updatedProposalRedline.conflictsCheckWaived)) {
      throw new Error("Conflicts Check Waived cannot be a Masked Contract");
    }

    if (
      selectedDocs.length &&
      !updatedProposalRedline.conflictsCheckWaived.currentEntry?.isEqualTo(new AHBoolean(false))
    ) {
      updatedProposalRedline = updatedProposalRedline.updateConflictsCheckWaivedRedline(
        updatedProposalRedline.conflictsCheckWaived.edit(new AHBoolean(false))
      );
    }

    for (const selectedDoc of selectedDocs) {
      if (Masked.isMasked(updatedProposalRedline.conflicts)) {
        throw new Error("Conflicts Documents cannot be a Masked Contract");
      }

      const workDocument = WorkDocument.fromDocument(selectedDoc, WorkDocumentType.Conflicts);
      if (
        !updatedProposalRedline.conflicts.redlines.some((document) => document.currentEntry?.is(workDocument))
      ) {
        updatedProposalRedline = updatedProposalRedline.updateConflictsRedline(
          updatedProposalRedline.conflicts.addEntry(workDocument)
        );
      }
    }

    for (const deselectedDoc of deselectedDocs) {
      if (Masked.isMasked(updatedProposalRedline.conflicts)) {
        throw new Error("Conflicts Documents cannot be a Masked Contract");
      }

      const redlineId = updatedProposalRedline.conflicts.redlines.find(
        (documentRedline) =>
          documentRedline.currentEntry?.id?.isEqualTo(deselectedDoc.id) ||
          (!documentRedline.currentEntry && documentRedline.revisedEntry?.id?.isEqualTo(deselectedDoc.id))
      )?.field.id;

      if (!redlineId) {
        continue;
      }

      updatedProposalRedline = updatedProposalRedline.updateConflictsRedline(
        updatedProposalRedline.conflicts.removeEntryByFieldId(redlineId)
      );
    }

    onProposalRedlineChange(updatedProposalRedline, null);
  }

  function renderDocumentSelector() {
    if (Masked.isMasked(proposalRedline.conflicts)) {
      throw new Error("Conflicts Documents cannot be a Masked Contract");
    }
    let selectedTemplateIds: Array<Guid> = [];
    for (const documentRedline of proposalRedline.conflicts.redlines) {
      if (documentRedline.currentEntry) {
        for (const templateId of documentRedline.currentEntry.templateIds) {
          selectedTemplateIds.push(templateId);
        }
      }
    }

    return (
      <DocumentSelector
        compact={selectorIsCompact}
        documentType="conflicts"
        proposalId={proposalId}
        selectedDocumentIds={proposalRedline.conflicts.currentIds}
        selectedTemplateIds={selectedTemplateIds}
        onDocumentSelectionChanged={handleDocumentSelectionChanged}
        onCreateDocumentFromTemplate={handleBeginCreateDocumentFromTemplate}
      />
    );
  }

  function handleConflictsCheckWaiverChanged(newRedline: FieldRedline<AHBoolean>) {
    try {
      onProposalRedlineChange(
        proposalRedline.updateConflictsCheckWaivedRedline(newRedline),
        ProposalField.WaiveConflictsCheck
      );
    } catch (error) {
      console.error(error);
      showErrorSnackbar(error, { doing: "updating the conflicts check waiver", operation: "proposal.conflicts.update-waiver" });
    }
  }

  async function handleBeginCreateDocumentFromTemplate(templateId: Guid | undefined | null) {
    if (!templateId) return;
    setCreatingDocumentFromTemplate(templateId);
    await handleDownloadDocumentById(templateId);
  }

  async function handleBeginReplaceDocument(documentId: Guid | undefined | null) {
    if (!documentId) throw new Error("Document ID must be present to replace document");
    setDocumentIdToReplace(documentId);
    await handleDownloadDocumentById(documentId);
  }

  function handleConflictsDocumentAdded(newDocument: Document) {
    if (Masked.isMasked(proposalRedline.conflicts)) {
      throw new Error("Conflicts Documents cannot be a Masked Contract");
    }

    const newWorkDocument = WorkDocument.fromDocument(
      newDocument,
      WorkDocumentType.Conflicts,
      documentIdToReplace ?? undefined,
      getReplacementDocumentTemplateIds(proposalRedline.conflicts, documentIdToReplace)
    );

    let updatedProposalRedline = proposalRedline;
    if (
      Masked.isMasked(updatedProposalRedline.conflicts) ||
      Masked.isMasked(updatedProposalRedline.conflictsCheckWaived)
    ) {
      throw new Error("Conflicts Documents cannot be a Masked Contract");
    }

    try {
      if (documentIdToReplace) {
        updatedProposalRedline = proposalRedline.updateConflictsRedline(
          proposalRedline.conflicts.replaceEntryById(documentIdToReplace, newWorkDocument)
        );
      } else if (
        !proposalRedline.conflicts.redlines.some((document) => document.currentEntry?.is(newWorkDocument))
      ) {
        updatedProposalRedline = proposalRedline.updateConflictsRedline(
          proposalRedline.conflicts.addEntry(newWorkDocument)
        );
      }

      if (Masked.isMasked(updatedProposalRedline.conflictsCheckWaived)) {
        throw new Error("Conflicts Check Waived cannot be a Masked Contract");
      }

      // The waiver as the proposal WILL stand, not the decision recorded on it. A waiver nobody has
      // decided on yet carries no `currentEntry`, and reading that alone as "still waiving" wrote an
      // edit to a field the user never touched: adding or replacing a conflicts document announced a
      // Waive Conflicts Check change of its own, and decided a question the counterparty had asked
      // (#5549). A check that is genuinely still being waived is cleared exactly as before — holding
      // conflicts documents and waiving the check are not compatible.
      if (!updatedProposalRedline.conflictsCheckWaived.resultingEntry?.isEqualTo(new AHBoolean(false))) {
        updatedProposalRedline = updatedProposalRedline.updateConflictsCheckWaivedRedline(
          updatedProposalRedline.conflictsCheckWaived.edit(new AHBoolean(false))
        );
      }

      onProposalRedlineChange(updatedProposalRedline, null);
      setDocumentIdToReplace(null);
      setCreatingDocumentFromTemplate(null);
    } catch (error) {
      console.error(error);
      showErrorSnackbar(error, { doing: "adding the conflicts document", operation: "proposal.conflicts.add-document" });
    }
  }

  if (Masked.isMasked(proposalRedline.conflictsCheckWaived) || Masked.isMasked(proposalRedline.conflicts)) {
    return maskedProposalFieldNotice(MaskedProposalField.Conflicts);
  }

  const conflictsCheckWaived =
    proposalRedline.conflictsCheckWaived.currentEntry?.value ??
    proposalRedline.conflictsCheckWaived.revisedEntry?.value ??
    false;
  const newlyAddedConflicts = proposalRedline.conflicts.redlines.some(
    (redline) => redline.isNewlyAdded
  );
  const showLockIcon = Boolean(disableEditing && fieldLockTooltip);
  return (
    <>
      <ListContainer container direction="row">
        {!disableEditing && (
          <>
            <DocumentSelectorContainer
              minimized={isDrawerOpen}
              selectedCount={proposalRedline.conflicts.currentIds.length}
              waived={conflictsCheckWaived}
            >
              {renderDocumentSelector()}
            </DocumentSelectorContainer>
            <SelectorDivider waived={conflictsCheckWaived}>
              <Divider orientation="vertical" />
            </SelectorDivider>
          </>
        )}
        <SelectedDocsPanel>
          <HeadControls>
            {(issues?.length ?? 0) > 0 && (
              <ProposalIssueList
                issues={issues ?? []}
                activeReviewField={activeReviewField}
                onIssueSelected={(issue) => onTraverseToNewField?.(issue.field)}
              />
            )}
            <SwitchControls>
              <Change
                active={activeReviewField?.isEqualTo(ProposalField.WaiveConflictsCheck) ?? false}
                className={traverserStopClassName(activeReviewField?.isEqualTo(ProposalField.WaiveConflictsCheck) ?? false)}
              >
                {showLockIcon && <ReviewerEditLockIcon title={fieldLockTooltip} />}
                <SwitchRedline
                  disabled={disableEditing}
                  boolRedline={proposalRedline.conflictsCheckWaived}
                  promptTitleText={newlyAddedConflicts ? "Remove Any Newly-Added Documents?" : undefined}
                  promptMessageText={
                    newlyAddedConflicts
                      ? "Waiving conflicts will remove any newly-added documents. Are you sure you want to proceed?"
                      : undefined
                  }
                  okButtonText={newlyAddedConflicts ? "Remove" : undefined}
                  unsetLabel="Not Waiving Conflicts Check"
                  setLabel="Waiving Conflicts Check"
                  onBoolRedlineChange={handleConflictsCheckWaiverChanged}
                />
              </Change>
              <FieldActions>
                {/* The waiver has a path of its own, /conflictsCheckWaived. The documents' history
                    heads their table instead, so this row's one history is the waiver's (#7074). */}
                <ProposalFieldHistoryButton
                  field={ProposalField.WaiveConflictsCheck}
                  negotiationId={negotiationId}
                  viewerScope={viewerScope}
                  toolTip="View conflicts check waiver change history"
                />
              </FieldActions>
            </SwitchControls>
          </HeadControls>
          <WaivedContent waived={conflictsCheckWaived}>
            <SelectedDocsList
              workDocumentType={WorkDocumentType.Conflicts}
              proposalId={proposalId}
              documentsRedline={proposalRedline.conflicts}
              downloadingFileId={isDownloadingFile}
              negotiationId={negotiationId}
              viewerScope={viewerScope}
              disableEditing={disableEditing}
              fieldLockTooltip={fieldLockTooltip}
              activeReviewField={activeReviewField}
              onDownload={handleDownloadDocumentById}
              onDocumentRedlineChange={(newRedline) => {
                try {
                  handleDocumentRedlineChanged(newRedline);
                } catch (error) {
                  console.error(error);
                  showErrorSnackbar(error, { doing: "updating the conflicts document", operation: "proposal.conflicts.update-document" });
                }
              }}
              onBeginReplaceDocument={handleBeginReplaceDocument}
              onDocumentClicked={(documentId: Guid) => {
                onTraverseToNewField?.(ProposalField.ConflictsDocument(documentId));
              }}
              onTraverseToNewField={onTraverseToNewField}
            />
          </WaivedContent>
        </SelectedDocsPanel>
      </ListContainer>
      {(creatingDocumentFromTemplate || documentIdToReplace) && (
        <Uploader
          documentType={WorkDocumentType.Conflicts}
          proposalId={proposalId}
          templateId={creatingDocumentFromTemplate ?? undefined}
          templateIds={
            creatingDocumentFromTemplate
              ? undefined
              : getReplacementDocumentTemplateIds(proposalRedline.conflicts, documentIdToReplace)
          }
          onDocumentUploaded={handleConflictsDocumentAdded}
          onClose={() => {
            setCreatingDocumentFromTemplate(null);
            setDocumentIdToReplace(null);
          }}
        />
      )}
    </>
  );
};

export default ConflictsTab;
